import { useTranslation } from "@/i18n/useTranslation";
import { Reveal } from "@/components/Reveal";
import { Code2, Search, ShieldCheck, FileCheck2, Check, Loader2 } from "lucide-react";

export function HowItWorks() {
  const { t } = useTranslation();
  const steps = [
    { Icon: Code2,       title: t("how.s1.title"), desc: t("how.s1.desc") },
    { Icon: Search,      title: t("how.s2.title"), desc: t("how.s2.desc") },
    { Icon: ShieldCheck, title: t("how.s3.title"), desc: t("how.s3.desc") },
    { Icon: FileCheck2,  title: t("how.s4.title"), desc: t("how.s4.desc") },
  ];
  return (
    <section id="how" className="py-20 md:py-28 scroll-mt-16 bg-[#FAFAFA] border-y border-border">
      <div className="container mx-auto max-w-[1440px] px-5 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t("how.eyebrow")}
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">{t("how.title")}</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{t("how.subtitle")}</p>
        </div>

        {/* Vertical alternating timeline */}
        <div className="relative max-w-3xl mx-auto">
          {/* center vertical line */}
          <div
            aria-hidden="true"
            className="absolute left-6 md:left-1/2 top-2 bottom-2 w-px bg-border md:-translate-x-1/2"
          />

          <div className="space-y-10 md:space-y-14">
            {steps.map(({ Icon, title, desc }, i) => {
              const isRight = i % 2 === 1;
              return (
                <Reveal
                  key={i}
                  delay={i * 80}
                  className="relative grid md:grid-cols-2 md:gap-12"
                >
                  {/* dot on the line */}
                  <div
                    aria-hidden="true"
                    className="absolute left-6 md:left-1/2 top-3 -translate-x-1/2 w-3 h-3 rounded-full bg-primary ring-4 ring-[#FAFAFA]"
                  />

                  {/* content */}
                  <div
                    className={`pl-16 md:pl-0 md:row-start-1 ${
                      isRight ? "md:col-start-2 md:pl-12" : "md:col-start-1 md:pr-12 md:text-right"
                    }`}
                  >
                    <div
                      className={`inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] text-primary/80 ${
                        isRight ? "" : "md:flex-row-reverse"
                      }`}
                    >
                      <span>SCHRITT {String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="mt-2 text-lg font-semibold text-foreground">{title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  </div>

                  {/* visual on opposite side, hugging the center line */}
                  <div
                    className={`hidden md:flex ${
                      isRight
                        ? "md:col-start-1 md:pr-12 md:justify-end"
                        : "md:col-start-2 md:pl-12 md:justify-start"
                    }`}
                  >
                    {i === 0 && (
                      <div
                        className={`inline-flex items-center justify-center gap-2 rounded-lg bg-[#0F172A] text-slate-100 font-mono text-[11px] px-4 py-2.5 min-w-[260px] ${
                          isRight ? "ml-auto" : ""
                        }`}
                      >
                        <span className="text-pink-400">&lt;script</span>
                        <span className="text-amber-300">src="..."</span>
                        <span className="text-pink-400">/&gt;</span>
                      </div>
                    )}
                    {i === 1 && (
                      <div
                        className={`inline-flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-2.5 shadow-sm min-w-[260px] ${
                          isRight ? "ml-auto" : ""
                        }`}
                      >
                        <span className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        </span>
                        <span className="text-[12px] text-foreground/80">
                          Scan läuft …
                          <span className="ml-1.5 text-muted-foreground">42 / 87 Seiten</span>
                        </span>
                      </div>
                    )}
                    {i === 2 && (
                      <div
                        className={`inline-flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-2.5 shadow-sm min-w-[260px] ${
                          isRight ? "ml-auto" : ""
                        }`}
                      >
                        <span className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-[12px] text-foreground/80 inline-flex items-center gap-1.5">
                          <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                          Banner aktiv
                        </span>
                      </div>
                    )}
                    {i === 3 && (
                      <div
                        className={`inline-flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-2.5 shadow-sm min-w-[260px] ${
                          isRight ? "ml-auto" : ""
                        }`}
                      >
                        <span className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                          <FileCheck2 className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-[12px] text-foreground/80">
                          Protokolliert
                          <span className="ml-1.5 text-muted-foreground font-mono">
                            #A8F2 · 14:32
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
